import { Controller } from "@hotwired/stimulus"

// Drag & drop (or click-to-pick) image upload for the gallery manager.
//
//   <div data-controller="uploader" data-uploader-url-value="/mng/galleries/1/images"
//        data-action="dragover->uploader#dragover dragleave->uploader#dragleave drop->uploader#drop">
//     <input type="file" multiple data-uploader-target="input" data-action="uploader#pick">
//     <p data-uploader-target="status"></p>
//   </div>
//
// Files are posted one request at a time so a single huge photo can't stall the
// whole batch, and the page reloads once everything succeeded so the new tiles
// (and their thumbnails) come from the server.
export default class extends Controller {
    static targets = ["input", "status", "progress"]
    static values = { url: String }
    static classes = ["active"]

    connect() {
        this.uploading = false
    }

    // --- drag & drop ---------------------------------------------------------

    dragover(event) {
        event.preventDefault()
        this.element.classList.add("is-dragover")
    }

    dragleave(event) {
        // Ignore drags moving between children of the drop zone.
        if (event.relatedTarget && this.element.contains(event.relatedTarget)) return
        this.element.classList.remove("is-dragover")
    }

    drop(event) {
        event.preventDefault()
        this.element.classList.remove("is-dragover")
        this.upload([...(event.dataTransfer?.files ?? [])])
    }

    // --- click to pick ------------------------------------------------------

    browse() {
        this.inputTarget.click()
    }

    pick() {
        this.upload([...this.inputTarget.files])
        this.inputTarget.value = "" // allow picking the same file again
    }

    // --- upload -------------------------------------------------------------

    async upload(files) {
        const images = files.filter((file) => file.type.startsWith("image/"))
        if (!images.length || this.uploading) return

        this.uploading = true
        let done = 0
        const failures = []

        for (const file of images) {
            this.report(`Lade hoch … ${done + 1}/${images.length} (${file.name})`, done / images.length)

            try {
                const body = new FormData()
                body.append("files[]", file)

                const response = await fetch(this.urlValue, {
                    method: "POST",
                    body,
                    headers: { "X-CSRF-Token": this.csrfToken, Accept: "application/json" },
                    credentials: "same-origin",
                })

                if (!response.ok) throw new Error(`HTTP ${response.status}`)
                const result = await response.json()
                if (result.failed?.length) failures.push(`${file.name}: ${result.failed[0].errors?.join(", ")}`)
            } catch (error) {
                failures.push(`${file.name}: ${error.message}`)
            }

            done += 1
        }

        this.uploading = false

        if (failures.length) {
            this.report(`Fehlgeschlagen: ${failures.join(" · ")}`, 1)
            return
        }

        this.report(`${done} Bild${done === 1 ? "" : "er"} hochgeladen — lade neu …`, 1)
        window.location.reload()
    }

    report(message, ratio) {
        if (this.hasStatusTarget) this.statusTarget.textContent = message
        if (this.hasProgressTarget) this.progressTarget.style.width = `${Math.round(ratio * 100)}%`
    }

    get csrfToken() {
        return document.querySelector('meta[name="csrf-token"]')?.content ?? ""
    }
}
